import {
	ObjectRendererBase,
	ObjectRendererInterface,
} from "@animech/axiom-scene-builder";
import { Entity, Vec3 } from "playcanvas";
import { Vector } from "@animech/axiom-format";
import { deepMergePartial, isObject } from "@animech/ui";
import { vectorToVec3 } from "@animech/axiom-utility";
import { assert } from "@animech/core-utilities";
import { Root } from "@definitions";
import { SceneObject } from "src/common";
import { isMobile } from "src/utility";
import { theme } from "src/ui/theme";
import { UnitSystem } from "src/core";

export type LinearMeasurement = {
	type: "linear";
	value: DynamicValue<string | null>;
	tags?: DynamicValue<string[]>;
	disabled?: DynamicValue<boolean>;

	start: DynamicValue<Vector>;
	end: DynamicValue<Vector>;

	style?: DynamicValue<DeepPartial<Style>>;

	/**
	 * Distance (sideways) to offset the line from the line formed by start -> end.
	 * A positive value offsets the line to left (when looking from start -> end).
	 * A negative value offsets the line to the right.
	 * This can also be a Vec3, in which case it's used directly as a world offset.
	 */
	lineOffset?: DynamicValue<number>;

	/**
	 * Represents the "normal" of the imaginary plane that the dimension is drawn on.
	 * The default value is Vec3.UP, which corresponds to measurements drawn on the ground.
	 * This direction affects how {lineOffset} is transformed into an actual 3D offset,
	 * since "left" and "right" offsets need a plane to relate to (and the measurement itself)
	 * in order to make sense.
	 */
	upDirection?: Vec3;

	/**
	 * Optional callback that will be executed once every frame,
	 * before any other property of this measurement is evaluated.
	 * This can be used as single point for updating cached values
	 * before rendering the measurement.
	 * This is only needed in cases where multiple properties need
	 * to be calculated together.
	 * Otherwise it's easier to just use dynamic values and update
	 * each property in its dynamic function.
	 */
	update?(rendererInterface: MeasurementRendererInterface): void;
};

export type AngularMeasurement = {
	type: "angular";
	value: DynamicValue<string | null>;
	tags?: DynamicValue<string[]>;
	disabled?: DynamicValue<boolean>;

	start: DynamicValue<Vector>;
	end: DynamicValue<Vector>;

	style?: DynamicValue<DeepPartial<Style>>;

	/**
	 * Boolean. When only two points are defined (annotation only), the text will
	 * normally be drawn on the right side of the line going from A to B.
	 * Set to true to force the text to be drawn on the left side.
	 */
	flipTextLocation?: DynamicValue<boolean>;

	/**
	 * Offset multiplier (number) for the text drawn at T1 / T2.
	 * The text is normally offset as much as is needed to not
	 * intersect the line / arc, which corresponds to a multiplier of 1.
	 * Modify multiplier to offset the text more or less from it's calculated position.
	 */
	textOffsetMult?: DynamicValue<number>;

	/**
	 * Optional callback that will be executed once every frame,
	 * before any other property of this measurement is evaluated.
	 * This can be used as single point for updating cached values
	 * before rendering the measurement.
	 * This is only needed in cases where multiple properties need
	 * to be calculated together.
	 * Otherwise it's easier to just use dynamic values and update
	 * each property in its dynamic function.
	 */
	update?(rendererInterface: MeasurementRendererInterface): void;
};

export type Measurement = LinearMeasurement | AngularMeasurement;

export type MeasurementRendererInterface = ObjectRendererInterface<Root>;

export type MeasurementType = Measurement["type"];

type ValueFactory<T> = (rendererInterface: MeasurementRendererInterface) => T;

export type DynamicValue<T> = T | ValueFactory<T>;

export type LineStyle = {
	width: number;
	dash: number[];
};

export type CapStyle = {
	size: number;
	fill: boolean;
};

export type PointStyle = {
	cap: DynamicValue<CapStyle | null>;
	/** Helper lines offseting the measurement line from its points */
	helpLine: DynamicValue<LineStyle | null>;
};

export type TextAlign = "left" | "right" | "center" | "start" | "end";

export type Baseline =
	| "top"
	| "hanging"
	| "middle"
	| "alphabetic"
	| "ideographic"
	| "bottom";

export type Style = {
	fontFamily: DynamicValue<string>;
	/** Pixel size */
	fontSize: DynamicValue<number>;
	textAlign: DynamicValue<TextAlign>;
	textBaseline: DynamicValue<Baseline>;
	line: DynamicValue<LineStyle>;
	/** Hexadecimal color */
	lineColor: DynamicValue<string>;
	startPointStyle: DynamicValue<PointStyle>;
	endPointStyle: DynamicValue<PointStyle>;
};

export type MeasurementFactory = (
	object: SceneObject,
	rendererInterface: MeasurementRendererInterface,
) => Measurement[] | undefined | void;

type DrawingContext = {
	canvas: HTMLCanvasElement;
	rendererInterface: ObjectRendererInterface<Root>;
};

type DeepPartial<T> =
	T extends Record<PropertyKey, unknown>
		? {
				[P in keyof T]?: DeepPartial<T[P]>;
			}
		: T extends () => infer R
			? () => DeepPartial<R>
			: T;

export class MeasurementRenderer extends ObjectRendererBase<Root> {
	private _realtimeCanvas: HTMLCanvasElement;
	private _factories = new Set<MeasurementFactory>();
	private _measurements = new Set<Measurement>();
	private _measurementObjects = new WeakMap<Measurement, object>();
	private _drawingContext?: DrawingContext;
	private _style: Style;
	private _visibleTags?: readonly string[];
	private _enabled = true;
	private _unitSystem: UnitSystem;
	private _groundLineColor?: string;

	constructor(
		realtimeCanvas: HTMLCanvasElement,
		enabled: boolean,
		unitSystem: UnitSystem,
	) {
		super();
		this._realtimeCanvas = realtimeCanvas;
		this._style = this._createDefaultStyle();
		this._enabled = enabled;
		this._unitSystem = unitSystem;
	}

	get unitSystem(): UnitSystem {
		return this._unitSystem;
	}

	set unitSystem(unitSystem: UnitSystem) {
		if (unitSystem !== this._unitSystem) {
			this._unitSystem = unitSystem;
			this.render();
		}
	}

	get enabled(): boolean {
		return this._enabled;
	}

	set enabled(enabled: boolean) {
		if (enabled !== this._enabled) {
			this._enabled = enabled;
			this.render();
		}
	}

	/**
	 * Hexadecimal color that, when set, replaces the line color of every linear
	 * measurement drawn on the ground (`upDirection` of Vec3.UP), including any
	 * color set by the measurement's own style.
	 */
	get groundLineColor(): string | undefined {
		return this._groundLineColor;
	}

	set groundLineColor(color: string | undefined) {
		if (color !== this._groundLineColor) {
			this._groundLineColor = color;
			this.render();
		}
	}

	get canvas(): HTMLCanvasElement | undefined {
		return this._drawingContext?.canvas;
	}

	get style(): Style {
		return this._style;
	}

	get visibleTags(): readonly string[] | undefined {
		return this._visibleTags;
	}

	set visibleTags(tags: readonly string[] | undefined) {
		this._visibleTags = tags;
		this.render();
	}

	add(
		measurement: Measurement | Iterable<Measurement>,
		associatedObject?: object,
	): this {
		const measurements =
			Symbol.iterator in measurement ? measurement : [measurement];
		for (const currentMeasurement of measurements) {
			if (this._measurements.has(currentMeasurement)) {
				console.warn("Measurement already exists");
				continue;
			}
			this._measurements.add(currentMeasurement);
			if (associatedObject) {
				this._measurementObjects.set(currentMeasurement, associatedObject);
			}
		}
		return this;
	}

	getAllForAssociatedObject(object: object): Measurement[] {
		return [...this._measurements].filter(
			(measurement) => this._measurementObjects.get(measurement) === object,
		);
	}

	delete(measurement: Measurement | Measurement[]): this {
		const measurements = Array.isArray(measurement)
			? measurement
			: [measurement];
		for (const currentMeasurement of measurements) {
			if (!this._measurements.has(currentMeasurement)) {
				continue;
			}
			this._measurements.delete(currentMeasurement);
			this._measurementObjects.delete(currentMeasurement);
		}
		return this;
	}

	deleteAllForAssociatedObject(object: object): this {
		const objectMeasurements = this.getAllForAssociatedObject(object);
		return this.delete(objectMeasurements);
	}

	registerFactory(factory: MeasurementFactory): this {
		this._factories.add(factory);
		return this;
	}

	onObjectBuilt(
		object: SceneObject,
		_entity: Entity,
		rendererInterface: ObjectRendererInterface<Root>,
	): void {
		const isRootObject = !object.parent;
		if (isRootObject) {
			rendererInterface.addSelectionDependency(object);
			this._initDrawingContext(rendererInterface);
			this.render();
		} else {
			for (const factory of this._factories) {
				const measurements = factory(object, rendererInterface);
				if (!measurements) {
					continue;
				}
				for (const measurement of measurements) {
					this.add(measurement, object);
				}
			}
		}
	}

	onObjectDeleted(object: SceneObject, _entity: Entity): void {
		this.deleteAllForAssociatedObject(object);
	}

	render() {
		if (this._drawingContext) {
			this._drawMeasurements(this._drawingContext);
		}
	}

	private _syncCanvasSize(
		canvas: HTMLCanvasElement,
		sourceCanvas: HTMLCanvasElement,
	) {
		const { offsetTop, offsetLeft, offsetWidth, offsetHeight, width, height } =
			sourceCanvas;
		if (
			canvas.offsetTop !== offsetTop ||
			canvas.offsetLeft !== offsetLeft ||
			canvas.offsetWidth !== offsetWidth ||
			canvas.offsetHeight !== offsetHeight ||
			canvas.width !== width ||
			canvas.height !== height
		) {
			canvas.style.top = `${offsetTop}px`;
			canvas.style.left = `${offsetLeft}px`;
			canvas.style.width = `${offsetWidth}px`;
			canvas.style.height = `${offsetHeight}px`;
			canvas.width = width;
			canvas.height = height;
		}
	}

	private _drawMeasurements({ canvas, rendererInterface }: DrawingContext) {
		this._syncCanvasSize(canvas, this._realtimeCanvas);

		const ctx = canvas.getContext("2d");
		if (!ctx) {
			return;
		}

		ctx.save();
		ctx.clearRect(0, 0, canvas.width, canvas.height);

		if (this._enabled) {
			const offsetSizeToCanvasSize = new Vec3(
				canvas.width / canvas.offsetWidth,
				canvas.height / canvas.offsetHeight,
				1,
			);
			const toScreen = (worldPosition: Vector) => {
				// HACK: Playcanvas uses client width and -height when transforming
				// to screen, instead of looking at the width and height attributes
				// for the canvas. We therefore need to scale the returned values to
				// match the attributes. In some cases the client size is much bigger
				// than the "logical" canvas size (set via width and height attributes).
				const screenPos = rendererInterface.cameraComponent
					.worldToScreen(vectorToVec3(worldPosition))
					.mul(offsetSizeToCanvasSize);
				screenPos.x = Math.round(screenPos.x);
				screenPos.y = Math.round(screenPos.y);
				return screenPos;
			};

			const baseStyle = this._resolveDeep(this._style);

			for (const measurement of this._measurements) {
				measurement.update?.(rendererInterface);

				if (this._resolve(measurement.disabled)) {
					continue;
				}
				if (this._visibleTags) {
					const tags = this._resolve(measurement.tags);
					if (!tags?.some((tag) => this._visibleTags?.includes(tag))) {
						continue;
					}
				}
				if (measurement.type === "linear") {
					this._drawLinearMeasurement(measurement, baseStyle, ctx, toScreen);
				} else if (measurement.type === "angular") {
					this._drawAngleMeasurement(measurement, baseStyle, ctx, toScreen);
				}
			}
		}

		ctx.restore();
	}

	private _drawLinearMeasurement(
		measurement: LinearMeasurement,
		baseStyle: DeepResolved<Style>,
		ctx: CanvasRenderingContext2D,
		toScreen: (position: Vector) => Vec3,
	) {
		const start = vectorToVec3(this._resolve(measurement.start));
		const end = vectorToVec3(this._resolve(measurement.end));
		const p1 = toScreen(start);
		const p2 = toScreen(end);

		// Skip the measurement if one of the points is behind the camera,
		// to avoid drawing infinite lines because of invalid world-to-screen projection.
		if (p1.z < 0 || p2.z < 0) {
			return;
		}

		let lp1 = p1;
		let lp2 = p2;

		const lineOffset = this._resolve(measurement.lineOffset);
		if (lineOffset) {
			const alongDir = end.clone().sub(start).normalize();
			const upDir = this._resolve(measurement.upDirection) ?? Vec3.UP;
			const offsetDir = new Vec3().cross(upDir, alongDir);
			const offset = offsetDir.mulScalar(lineOffset);

			lp1 = toScreen(start.clone().add(offset));
			lp2 = toScreen(end.clone().add(offset));
		}

		const style = measurement.style
			? deepMergePartial([baseStyle, this._resolveDeep(measurement.style)])
			: baseStyle;

		const isOnGround = (measurement.upDirection ?? Vec3.UP).equals(Vec3.UP);
		const lineColor =
			isOnGround && this._groundLineColor
				? this._groundLineColor
				: style.lineColor;

		ctx.save();
		ctx.fillStyle = lineColor;
		ctx.strokeStyle = lineColor;
		ctx.lineWidth = style.line.width;

		const canvasHeight = ctx.canvas.height;
		const capSize = Math.max(canvasHeight / 60, 16);
		const relX = lp2.x - lp1.x;
		const relY = lp2.y - lp1.y;
		const length = Math.sqrt(relX * relX + relY * relY);
		const dirX = relX / length;
		const dirY = relY / length;
		const capDirX = dirX * capSize;
		const capDirY = dirY * capSize;

		if (style.startPointStyle.helpLine) {
			ctx.lineWidth = style.startPointStyle.helpLine.width;
			ctx.setLineDash(style.startPointStyle.helpLine.dash);
			ctx.beginPath();
			ctx.moveTo(p1.x, p1.y);
			ctx.lineTo(lp1.x, lp1.y);
			ctx.stroke();
		}

		if (style.endPointStyle.helpLine) {
			ctx.lineWidth = style.endPointStyle.helpLine.width;
			ctx.setLineDash(style.endPointStyle.helpLine.dash);
			ctx.beginPath();
			ctx.moveTo(p2.x, p2.y);
			ctx.lineTo(lp2.x, lp2.y);
			ctx.stroke();
		}

		ctx.lineWidth = style.line.width;
		ctx.setLineDash(style.line.dash);

		const dimText = this._resolve(measurement.value) ?? "";
		const textWidth = ctx.measureText(dimText).width;

		ctx.font = `${style.fontSize}px ${style.fontFamily}`;
		ctx.textAlign = style.textAlign;
		ctx.textBaseline = style.textBaseline;
		ctx.fillText(dimText, lp1.x + relX * 0.5, lp1.y + relY * 0.5);

		// line
		// use a gap that's bigger than the text (depending on dir)
		const gapWidthDirFactor = Math.abs(dirX);
		const gapWidth =
			gapWidthDirFactor * textWidth + (2 - gapWidthDirFactor) * style.fontSize;
		const gapLen = Math.min(gapWidth * 1.2, length * 0.7);

		// When drawing with alpha, the line can not clip the cap or it will show through
		const lp1CapAdjusted =
			style.startPointStyle.cap?.size === undefined
				? lp1
				: {
						x: lp1.x + capDirX * style.startPointStyle.cap.size,
						y: lp1.y + capDirY * style.startPointStyle.cap?.size,
					};
		const lp2CapAdjusted =
			style.endPointStyle.cap?.size === undefined
				? lp2
				: {
						x: lp2.x - capDirX * style.endPointStyle.cap.size,
						y: lp2.y - capDirY * style.endPointStyle.cap.size,
					};

		if (dimText.length > 0) {
			const lineLen = (length - gapLen) * 0.5;
			ctx.beginPath();
			ctx.moveTo(lp1CapAdjusted.x, lp1CapAdjusted.y);
			ctx.lineTo(lp1.x + dirX * lineLen, lp1.y + dirY * lineLen);
			ctx.stroke();

			ctx.beginPath();
			ctx.moveTo(lp2CapAdjusted.x, lp2CapAdjusted.y);
			ctx.lineTo(lp2.x - dirX * lineLen, lp2.y - dirY * lineLen);
			ctx.stroke();
		} else {
			ctx.beginPath();
			ctx.moveTo(lp1CapAdjusted.x, lp1CapAdjusted.y);
			ctx.lineTo(lp2CapAdjusted.x, lp2CapAdjusted.y);
			ctx.stroke();
		}

		if (style.startPointStyle.cap) {
			const { cap } = style.startPointStyle;
			ctx.beginPath();
			ctx.moveTo(lp1.x, lp1.y);
			ctx.lineTo(
				lp1.x + (capDirX + capDirY * 0.5) * cap.size,
				lp1.y + (capDirY - capDirX * 0.5) * cap.size,
			);
			ctx.lineTo(
				lp1.x + (capDirX - capDirY * 0.5) * cap.size,
				lp1.y + (capDirY + capDirX * 0.5) * cap.size,
			);
			if (cap.fill) {
				ctx.fill();
			} else {
				ctx.closePath();
				ctx.stroke();
			}
		}

		if (style.endPointStyle.cap) {
			const { cap } = style.endPointStyle;
			ctx.beginPath();
			ctx.moveTo(lp2.x, lp2.y);
			ctx.lineTo(
				lp2.x - (capDirX + capDirY * 0.5) * cap.size,
				lp2.y - (capDirY - capDirX * 0.5) * cap.size,
			);
			ctx.lineTo(
				lp2.x - (capDirX - capDirY * 0.5) * cap.size,
				lp2.y - (capDirY + capDirX * 0.5) * cap.size,
			);
			if (cap.fill) {
				ctx.fill();
			} else {
				ctx.closePath();
				ctx.stroke();
			}
		}

		ctx.restore();
	}

	private _drawAngleMeasurement(
		measurement: AngularMeasurement,
		baseStyle: DeepResolved<Style>,
		ctx: CanvasRenderingContext2D,
		toScreen: (position: Vector) => Vec3,
	) {
		const vA = vectorToVec3(this._resolve(measurement.start));
		const vB = vectorToVec3(this._resolve(measurement.end));
		const vDeltaBA = vA.clone().sub(vB);
		const vDirBA = vDeltaBA.clone().normalize();
		const legLength = vDeltaBA.length();
		const vMidBA = vB.clone().add(vDirBA.clone().mulScalar(legLength / 2));

		const scrA = toScreen(vA);
		const scrMidBA = toScreen(vMidBA);

		const style = measurement.style
			? deepMergePartial([baseStyle, this._resolveDeep(measurement.style)])
			: baseStyle;

		const text = this._resolve(measurement.value) ?? "";
		const textOffsetMult = this._resolve(measurement.textOffsetMult) || 1;
		const textPixelOffset = 10;
		const textOuterHeight = style.fontSize + textPixelOffset * 2;
		let textOffsetLength =
			textOffsetMult * ((textOuterHeight - style.fontSize) / 2);
		let canvasRotation = Math.atan2(scrA.y - scrMidBA.y, scrA.x - scrMidBA.x);

		if (scrA.x < scrMidBA.x) {
			canvasRotation += Math.PI;
		}

		if (this._resolve(measurement.flipTextLocation)) {
			textOffsetLength = -textOffsetLength;
		}

		ctx.save();
		ctx.translate(scrMidBA.x, scrMidBA.y);
		ctx.rotate(canvasRotation);
		ctx.font = `${style.fontSize}px ${style.fontFamily}`;
		ctx.textAlign = style.textAlign;
		ctx.textBaseline = style.textBaseline;
		ctx.fillText(text, 0, -textOffsetLength);
		ctx.restore();
	}

	private _createDefaultStyle(): Style {
		let fontSize: Style["fontSize"] = 16;
		let capSize: CapStyle["size"] = 0.75;

		if (isMobile()) {
			fontSize = 14;
			capSize = 0.4;
		}
		return {
			fontFamily: theme.typography.fontFamily.primary,
			fontSize,
			textAlign: "center",
			textBaseline: "middle",
			line: { width: 1.0, dash: [] },
			lineColor: "#000",
			startPointStyle: {
				cap: {
					size: capSize,
					fill: true,
				},
				helpLine: {
					width: 0.5,
					dash: [1, 3],
				},
			},
			endPointStyle: {
				cap: {
					size: capSize,
					fill: true,
				},
				helpLine: {
					width: 0.5,
					dash: [1, 3],
				},
			},
		};
	}

	private _initDrawingContext(
		rendererInterface: ObjectRendererInterface<Root>,
	) {
		if (this._drawingContext) {
			this._drawingContext.rendererInterface = rendererInterface;
		}

		if (this._drawingContext || !this._realtimeCanvas.parentElement) {
			return;
		}

		const canvas = document.createElement("canvas");
		canvas.id = "measurements";
		canvas.style.position = "absolute";
		canvas.style.pointerEvents = "none";
		this._realtimeCanvas.parentElement.appendChild(canvas);

		this._drawingContext = { canvas, rendererInterface };
	}

	private _resolve<T>(value: T): Resolved<T>;
	private _resolve<U, T extends U>(value: U, fallback: T): Resolved<T>;
	private _resolve<T>(...chain: T[]): Resolved<T> {
		assert(this._drawingContext);
		const { rendererInterface } = this._drawingContext;

		for (const link of chain) {
			const value =
				typeof link === "function"
					? (link as ValueFactory<T>)(rendererInterface)
					: link;
			if (value !== undefined) {
				return value as Resolved<T>;
			}
		}
		return undefined as Resolved<T>;
	}

	private _resolveDeep<T>(input: T): DeepResolved<T> {
		const value = this._resolve(input);
		if (isObject(value)) {
			return Object.entries(value).reduce(
				(acc, [key, property]) => ({
					...acc,
					[key]: this._resolveDeep(property),
				}),
				{} as DeepResolved<T> & Record<PropertyKey, unknown>,
			);
		} else {
			return value as DeepResolved<T>;
		}
	}
}

type Resolved<T> = T extends ValueFactory<infer R> ? R : T;

type DeepResolved<T> = [Resolved<T>] extends [infer R]
	? R extends Record<PropertyKey, unknown>
		? {
				[P in keyof R]: DeepResolved<R[P]>;
			}
		: R
	: never;
